import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SkeletonCard } from '../components/Skeleton/Skeleton';
import { useHistoricalData } from '../hooks/useHistoricalData';
import { usePageMeta } from '../hooks/usePageMeta';
import { useSnapshotComparison } from '../hooks/useSnapshotComparison';
import { useUnifiedModels } from '../hooks/useUnifiedModels';
import { trackEvent } from '../services/analytics';
import { getActiveCatalogOfferingCount } from '../services/catalog';
import {
  getModelBestForText,
  getModelCapabilityLabels,
  getModelRegionLabel,
  getModelResidencySummary,
} from '../utils/modelDiscovery';
import { formatModelDisplayName } from '../utils/modelFormatting';
import type { UnifiedModel } from '../utils/unifiedModels';

const QUICK_SEARCHES = ['agentic coding', 'vision', 'long context', 'EU residency'];

const DECISION_PATHS: Array<{
  step: string;
  title: string;
  detail: string;
  to: string;
  cta: string;
  icon: ReactNode;
}> = [
  {
    step: '01',
    title: 'Discover viable models',
    detail: 'Start with capability, publisher, region, and residency requirements.',
    to: '/models',
    cta: 'Explore the catalog',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m12 3.5 7 4v8l-7 4-7-4v-8l7-4Zm0 8 7-4M12 11.5l-7-4m7 4v8"
      />
    ),
  },
  {
    step: '02',
    title: 'Compare deployments',
    detail: 'See the same model across platforms, pricing, latency, and routing options.',
    to: '/compare/models',
    cta: 'Build a comparison',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 5v14M17 5v14M4.5 8 7 5l2.5 3M14.5 16 17 19l2.5-3"
      />
    ),
  },
  {
    step: '03',
    title: 'Verify the evidence',
    detail: 'Review residency boundaries, provider claims, and measured operational signals.',
    to: '/compliance/residency',
    cta: 'Check residency',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3.5 18.5 6v5.3c0 4.1-2.7 7-6.5 8.7-3.8-1.7-6.5-4.6-6.5-8.7V6L12 3.5Zm-2.5 8.3 1.7 1.7 3.5-3.7"
      />
    ),
  },
];

const TASK_LINKS = [
  {
    label: 'Find EU-ready deployments',
    detail: 'Residency and regional controls',
    href: '/guides/eu-ai-models',
  },
  {
    label: 'Compare Bedrock and direct APIs',
    detail: 'Routing and platform trade-offs',
    href: '/compare/bedrock-vs-anthropic',
  },
  {
    label: 'Review HIPAA evidence',
    detail: 'Provider compliance coverage',
    href: '/guides/hipaa-ai-model-providers',
  },
  { label: 'Generate IAM controls', detail: 'Bedrock access policy', href: '/governance/aws' },
];

function getProviderMark(provider: string): string {
  return provider
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function formatRegionSummary(model: UnifiedModel): string {
  const regionLabel = getModelRegionLabel(model);
  return /^\d+$/.test(regionLabel) ? `${regionLabel} regions` : regionLabel;
}

function Metric({ value, label, testId }: { value: number; label: string; testId?: string }) {
  return (
    <div className="min-w-0 border-l border-white/15 px-4 first:border-l-0 first:pl-0 sm:px-5">
      <div data-testid={testId} className="text-2xl font-bold tracking-[-0.04em] text-white">
        {value > 0 ? value.toLocaleString() : '—'}
      </div>
      <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-400">
        {label}
      </div>
    </div>
  );
}

function ModelReleaseRow({ model }: { model: UnifiedModel }) {
  const residencySummary = getModelResidencySummary(model);

  return (
    <Link
      to={`/models/${encodeURIComponent(model.slug)}`}
      data-testid="model-card"
      className="group grid gap-4 px-5 py-5 transition hover:bg-blue-50/50 lg:grid-cols-[minmax(210px,1.2fr)_minmax(180px,1fr)_140px_130px] lg:items-center"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-xs font-bold text-slate-700 transition group-hover:border-blue-200 group-hover:bg-white group-hover:text-blue-700">
          {getProviderMark(model.provider)}
        </span>
        <div className="min-w-0">
          <div className="truncate font-bold text-slate-950 group-hover:text-blue-700">
            <span data-testid="model-card-link">{formatModelDisplayName(model.displayName)}</span>
          </div>
          <div className="mt-1 text-xs font-medium text-slate-500">{model.provider}</div>
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 lg:hidden">
          Best for
        </div>
        <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-600 lg:mt-0">
          {getModelBestForText(model)}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {getModelCapabilityLabels(model, 2).map((label) => (
          <span key={label} className="console-chip">
            {label}
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 lg:block">
        <div className="text-sm font-bold text-slate-800">
          {model.deployments.length} deployments
          <div className="mt-0.5 text-xs font-medium text-slate-500">
            {formatRegionSummary(model)}
          </div>
        </div>
        <span className={`console-chip shrink-0 lg:mt-2 ${residencySummary.className}`}>
          {residencySummary.label}
        </span>
      </div>
    </Link>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { newestModels, providerCounts, unifiedModels, isLoading } = useUnifiedModels();
  const { currentSnapshot } = useHistoricalData();
  const { comparison } = useSnapshotComparison();
  const newCount = comparison?.newCount ?? 0;
  const totalOfferings = getActiveCatalogOfferingCount(currentSnapshot);
  const totalRegions =
    currentSnapshot?.metadata.regionsSucceeded?.length ??
    currentSnapshot?.metadata.totalRegions ??
    0;

  usePageMeta({
    title: 'ModelMap - AI Deployment Intelligence',
    description:
      'Compare catalogued AI model deployments by provider, region, price, and residency evidence.',
    canonicalPath: '/',
  });

  const inventoryRows = newestModels.slice(0, 5);

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = searchQuery.trim();
    navigate(trimmed ? `/models?q=${encodeURIComponent(trimmed)}` : '/models');
  };

  return (
    <div className="console-page">
      <section
        data-testid="hero-section"
        className="overflow-hidden rounded-2xl bg-slate-950 text-white shadow-xl shadow-slate-300/30"
      >
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="relative overflow-hidden p-6 sm:p-8 lg:p-10 xl:p-12">
            <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-36 left-1/3 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-blue-100">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
                Cross-provider deployment catalog
              </div>
              <h1
                data-testid="hero-headline"
                className="mt-5 max-w-3xl text-4xl font-bold leading-[1.04] tracking-[-0.045em] text-white sm:text-5xl xl:text-[58px]"
              >
                Compare AI model deployments across providers.
              </h1>
              <p
                data-testid="hero-description"
                className="mt-5 max-w-2xl text-base leading-7 text-slate-300 sm:text-lg"
              >
                See provider, region, price, and residency evidence for each catalogued deployment.
              </p>

              <form onSubmit={handleSearchSubmit} className="mt-8 max-w-3xl">
                <div className="flex flex-col gap-2 rounded-xl bg-white p-2 shadow-2xl shadow-slate-950/30 sm:flex-row">
                  <div className="relative min-w-0 flex-1">
                    <svg
                      className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                      />
                    </svg>
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      aria-label="Search models by name, capability, provider, or region"
                      placeholder="Search a model, capability, provider, or region"
                      className="h-13 w-full rounded-lg border-0 bg-white pl-12 pr-4 text-sm text-slate-950 outline-none placeholder:text-slate-400 focus:ring-4 focus:ring-blue-100"
                    />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex min-h-13 items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 text-sm font-bold text-white transition hover:bg-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-300/50"
                  >
                    Search catalog
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="m9 5 7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <span className="font-semibold text-slate-300">Popular:</span>
                  {QUICK_SEARCHES.map((search) => (
                    <Link
                      key={search}
                      to={`/models?q=${encodeURIComponent(search)}`}
                      className="min-h-7 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 font-medium text-slate-300 transition hover:border-blue-300/50 hover:bg-blue-400/10 hover:text-white"
                    >
                      {search}
                    </Link>
                  ))}
                </div>
              </form>

              <div data-testid="hero-cta" className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/models"
                  data-testid="browse-models-button"
                  className="inline-flex min-h-11 items-center justify-center rounded-lg bg-white px-5 text-sm font-bold text-slate-950 transition hover:bg-blue-50"
                >
                  Browse models
                </Link>
                <Link
                  to="/compare/models"
                  data-testid="compare-platforms-button"
                  className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/20 bg-white/5 px-5 text-sm font-bold text-white transition hover:bg-white/10"
                >
                  Compare deployments
                </Link>
              </div>

              <div
                data-testid="quick-stats"
                className="mt-10 grid grid-cols-2 gap-y-5 border-t border-white/10 pt-6 sm:grid-cols-4"
              >
                <Metric value={unifiedModels.length} label="Models" testId="stat-models-count" />
                <Metric
                  value={providerCounts.length}
                  label="Publishers"
                  testId="stat-providers-count"
                />
                <Metric value={totalOfferings} label="Offerings" />
                <Metric value={totalRegions} label="Regions" testId="stat-regions-count" />
              </div>
            </div>
          </div>

          <aside
            data-testid="home-decision-sidebar"
            className="hidden border-l border-white/10 bg-white/[0.035] p-8 lg:flex lg:flex-col lg:justify-between"
          >
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-blue-300">
                A better model decision
              </div>
              <h2 className="mt-3 text-2xl font-bold tracking-[-0.03em] text-white">
                A model name is only the start.
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-400">
                ModelMap connects the model to the deployment details that determine whether you can
                use it.
              </p>
              <div className="mt-7 space-y-5">
                {[
                  ['Availability', 'Platforms and regions'],
                  ['Economics', 'Pricing and measured latency'],
                  ['Assurance', 'Residency and validation evidence'],
                ].map(([title, detail], index) => (
                  <div key={title} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-xs font-bold text-blue-300">
                      {index + 1}
                    </span>
                    <div>
                      <div className="text-sm font-bold text-white">{title}</div>
                      <div className="mt-0.5 text-xs text-slate-400">{detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-blue-300 hover:text-blue-200"
            >
              How the evidence works
              <span aria-hidden="true">→</span>
            </Link>
          </aside>
        </div>
      </section>

      <section data-testid="newest-models-section" className="console-panel overflow-hidden">
        <div className="console-panel-header sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <h2 data-testid="newest-models-heading" className="console-panel-title">
                Newest in this snapshot
              </h2>
              {newCount > 0 && (
                <span className="console-chip console-chip-blue">{newCount} new</span>
              )}
            </div>
            <p data-testid="newest-models-description" className="console-panel-kicker">
              Models ordered by the release metadata in the repository snapshot.
            </p>
          </div>
          <Link
            to="/models?sort=newest"
            data-testid="view-all-models-link"
            className="inline-flex min-h-9 items-center gap-1 rounded-lg px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-50"
          >
            View all models <span aria-hidden="true">→</span>
          </Link>
        </div>
        {isLoading ? (
          <div
            data-testid="newest-models-skeleton"
            className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3"
          >
            {[...Array(3)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : (
          <div data-testid="newest-models-grid" className="divide-y divide-slate-100">
            <div className="hidden bg-slate-50/80 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500 lg:grid lg:grid-cols-[minmax(210px,1.2fr)_minmax(180px,1fr)_140px_130px]">
              <span>Model</span>
              <span>Best for</span>
              <span>Capabilities</span>
              <span>Deployment</span>
            </div>
            {inventoryRows.map((model) => (
              <ModelReleaseRow key={model.slug} model={model} />
            ))}
          </div>
        )}
      </section>

      <section data-testid="decision-shortcuts-section">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-blue-600">
              Compare the details that matter
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-[-0.03em] text-slate-950">
              Narrow the catalog in three steps
            </h2>
          </div>
          <p className="max-w-xl text-sm leading-6 text-slate-600">
            Start with a model, then compare the provider, region, price, and published evidence.
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {DECISION_PATHS.map((path) => (
            <Link
              key={path.step}
              to={path.to}
              className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/30 transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-100/50 sm:p-6"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700 transition group-hover:bg-blue-600 group-hover:text-white">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    aria-hidden="true"
                  >
                    {path.icon}
                  </svg>
                </span>
                <span className="text-xs font-bold tracking-[0.1em] text-slate-300">
                  {path.step}
                </span>
              </div>
              <h3 className="mt-6 text-lg font-bold tracking-[-0.02em] text-slate-950">
                {path.title}
              </h3>
              <p className="mt-2 min-h-12 text-sm leading-6 text-slate-600">{path.detail}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-blue-700">
                {path.cta}{' '}
                <span aria-hidden="true" className="transition group-hover:translate-x-1">
                  →
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
        <div className="console-panel overflow-hidden">
          <div className="console-panel-header">
            <div>
              <h2 className="console-panel-title">Common decision paths</h2>
              <p className="console-panel-kicker">Start with the constraint you already know.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2">
            {TASK_LINKS.map((task, index) => (
              <Link
                key={task.href}
                to={task.href}
                onClick={() =>
                  trackEvent('home_task_selected', { task: task.label, destination: task.href })
                }
                className={`group flex min-h-28 items-center justify-between gap-4 p-5 transition hover:bg-blue-50/60 ${index % 2 === 0 ? 'sm:border-r sm:border-slate-100' : ''} ${index < 2 ? 'border-b border-slate-100' : ''}`}
              >
                <div>
                  <div className="text-sm font-bold text-slate-950 group-hover:text-blue-700">
                    {task.label}
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">{task.detail}</div>
                </div>
                <span
                  className="text-slate-300 transition group-hover:translate-x-1 group-hover:text-blue-600"
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="console-panel p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="console-panel-title">Coverage at a glance</h2>
              <p className="console-panel-kicker">Top tracked model publishers.</p>
            </div>
            <Link to="/providers" className="text-xs font-bold text-blue-700 hover:text-blue-900">
              All providers
            </Link>
          </div>
          <div className="mt-6 space-y-4">
            {providerCounts.slice(0, 5).map(({ provider, count }) => {
              const width = providerCounts[0]?.count
                ? Math.max(10, (count / providerCounts[0].count) * 100)
                : 0;
              return (
                <Link
                  key={provider}
                  to={`/models?providers=${encodeURIComponent(provider)}`}
                  className="block"
                >
                  <div className="mb-1.5 flex items-center justify-between text-xs font-semibold">
                    <span className="text-slate-700">{provider}</span>
                    <span className="text-slate-400">{count}</span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-600 to-cyan-400"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
